import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  PowerSettingsNew as DeactivateIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApiQuery } from '../hooks/useApiQuery';
import { costCentersApi } from '../services/costCentersService';
import { CostCenterDefinition } from '../services/types';
import { TableSkeleton } from '../components/SkeletonLoaders/TableSkeleton';
import ErrorMessage from '../components/ErrorMessage';
import { CostCenterEditorDialog, CostCenterFormValues } from '../components/CostCenterEditorDialog';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
}

const TYPE_LABELS: Record<string, string> = {
  fachbereich: 'Fachbereich',
  plattform: 'Plattform',
};

export default function CostCentersPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const { data: costCenters, loading, error, refetch } = useApiQuery<CostCenterDefinition[]>(
    () => costCentersApi.list(),
    [isAdmin],
    { enabled: isAdmin }
  );

  const [editorCc, setEditorCc] = useState<CostCenterDefinition | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));
  const openCreate = () => setEditorCc(null);
  const openEdit = (cc: CostCenterDefinition) => setEditorCc(cc);
  const closeEditor = () => setEditorCc(undefined);

  const handleSubmit = async (values: CostCenterFormValues) => {
    setSaving(true);
    try {
      if (editorCc === null) {
        // Create mode
        await costCentersApi.create(values);
        setSnackbar({ open: true, message: `Kostenstelle "${values.code}" wurde angelegt.`, severity: 'success' });
      } else if (editorCc !== undefined) {
        // Edit mode — code is immutable, so it is never part of the update payload.
        await costCentersApi.update(editorCc.code, {
          displayName: values.displayName,
          type: values.type,
          ownerEmail: values.ownerEmail,
          budgetMonthlyEur: values.budgetMonthlyEur,
          validFrom: values.validFrom,
          validTo: values.validTo,
          active: values.active,
        });
        setSnackbar({ open: true, message: `Kostenstelle "${editorCc.code}" wurde aktualisiert.`, severity: 'success' });
      }
      closeEditor();
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler.';
      setSnackbar({ open: true, message: `Fehler: ${message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (cc: CostCenterDefinition) => {
    try {
      await costCentersApi.delete(cc.code);
      setSnackbar({ open: true, message: `Kostenstelle "${cc.code}" wurde deaktiviert.`, severity: 'success' });
      await refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler.';
      setSnackbar({ open: true, message: `Fehler: ${message}`, severity: 'error' });
    }
  };

  if (!isAdmin) {
    return (
      <Box>
        <Alert severity="error">
          Zugriff verweigert — diese Seite ist nur fuer Admins zugaenglich.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Kostenstellen</Typography>
          <Typography variant="body2" color="text.secondary">
            Stammliste der Kostenstellen (FinOps) inkl. inaktiver Eintraege.
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={loading}
          >
            Kostenstelle anlegen
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refetch}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {loading && <TableSkeleton rows={4} columns={6} />}

      {!loading && error && (
        <ErrorMessage
          message={error}
          title="Kostenstellen konnten nicht geladen werden"
          onRetry={refetch}
        />
      )}

      {!loading && !error && costCenters && costCenters.length === 0 && (
        <Alert severity="info">
          Noch keine Kostenstellen angelegt. Klicken Sie auf{' '}
          <strong>Kostenstelle anlegen</strong>, um die erste anzulegen.
        </Alert>
      )}

      {!loading && !error && costCenters && costCenters.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small" aria-label="Kostenstellen-Tabelle">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Anzeigename</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Budget/Monat</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {costCenters.map((cc) => (
                <TableRow key={cc.code} hover>
                  <TableCell>{cc.code}</TableCell>
                  <TableCell>{cc.displayName}</TableCell>
                  <TableCell>{TYPE_LABELS[cc.type] ?? cc.type}</TableCell>
                  <TableCell>
                    {cc.budgetMonthlyEur !== undefined && cc.budgetMonthlyEur !== null
                      ? `${cc.budgetMonthlyEur} €`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {!cc.active && <Chip label="Inaktiv" size="small" />}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={`${cc.code} bearbeiten`}>
                      <IconButton
                        size="small"
                        aria-label={`${cc.code} bearbeiten`}
                        onClick={() => openEdit(cc)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {cc.active && (
                      <Tooltip title={`${cc.code} deaktivieren`}>
                        <IconButton
                          size="small"
                          aria-label={`${cc.code} deaktivieren`}
                          onClick={() => handleDeactivate(cc)}
                        >
                          <DeactivateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CostCenterEditorDialog
        open={editorCc !== undefined}
        costCenter={editorCc ?? null}
        saving={saving}
        onSubmit={handleSubmit}
        onClose={closeEditor}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
